import type { SwiperOptions } from 'swiper/types';

import React, { useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'react-feather';
import { FreeMode, Navigation, Thumbs } from 'swiper/modules';
import {
  Swiper,
  type SwiperClass,
  type SwiperRef,
  SwiperSlide,
} from 'swiper/react';

import 'swiper/css';

import { type GalleryModalTabPanelProps } from './data';

import styles from './index.module.scss';

import {
  Box,
  Icon,
  IconButton,
  Image,
  Stack,
  TabPanel,
  Text,
} from '@/components';
import { useTranslation } from '@/hooks';

const GalleryThumbNailImage: React.FC<{
  img: string;
  onClick?: () => void;
}> = ({ img, onClick }) => {
  return (
    <Box
      display="flex"
      justifyContent="center"
      key={img}
      marginBlock="5px"
      sx={{
        borderRadius: 'xs',
        w: { md: '83px' },
        h: { md: '73px' },
        bg: 'gray.50',
        border: '1px solid',
        borderColor: 'primary.50',
        '.img': {
          width: '100%',
          borderRadius: 'xs',
          w: { md: '73px' },
          h: { md: '73px' },
          objectFit: 'cover',
        },
      }}
    >
      <Image
        src={img}
        alt="image"
        className="img"
        width={83}
        height={83}
        onClick={onClick}
      />
    </Box>
  );
};

const GalleryModalTabPanel: GalleryModalTabPanelProps = ({
  galleryImages,
  initialSlide,
}) => {
  const { t } = useTranslation('pages.products');
  const swiperRef = useRef<SwiperRef>(null);
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperClass>();
  const [current, setCurrent] = useState<number>(1);
  const settings: SwiperOptions = {
    initialSlide: initialSlide,
    slidesPerView: 'auto',
    navigation: {
      prevEl: '.swiper-button-prev',
      nextEl: '.swiper-button-next',
    },
  };
  const prevSlide = () => {
    swiperRef.current?.swiper.slidePrev();
  };

  const nextSlide = () => {
    swiperRef.current?.swiper.slideNext();
  };

  return (
    <TabPanel padding="0" minHeight="360px">
      {galleryImages.length === 0 && <Text>{t('noPhotosYet')}</Text>}
      {galleryImages.length > 0 && (
        <>
          <Text
            display={{ md: 'none' }}
            fontWeight={700}
            textAlign="center"
            mb="2"
            mt="1"
          >
            {current}/{galleryImages.length}
          </Text>
          <Stack
            direction={{ base: 'column', md: 'row' }}
            gap={{ base: '2', md: '26px' }}
          >
            <Box
              sx={{ maxW: { base: '450px', md: '450px' } }}
              position="relative"
            >
              <Swiper
                ref={swiperRef}
                watchSlidesProgress={true}
                {...settings}
                dir="rtl"
                hashNavigation={{
                  watchState: true,
                }}
                thumbs={{
                  swiper:
                    thumbsSwiper && !thumbsSwiper.destroyed
                      ? thumbsSwiper
                      : null,
                }}
                modules={[FreeMode, Navigation, Thumbs]}
                className={styles.galleryModalSwiper}
                onRealIndexChange={(element) =>
                  setCurrent(element.activeIndex + 1)
                }
              >
                {galleryImages?.map((img) => (
                  <SwiperSlide key={img}>
                    <Box
                      marginInline={{ base: '5px', md: 0 }}
                      sx={{
                        bg: 'gray.50',
                        borderRadius: 'xs',
                        '.img': {
                          borderRadius: 'xs',
                        },
                      }}
                    >
                      <Image
                        src={img}
                        alt={img}
                        className="img"
                        width={450}
                        height={450}
                        priority
                      />
                    </Box>
                  </SwiperSlide>
                ))}
                {current !== 1 && (
                  <IconButton
                    backgroundColor="primary.50"
                    aria-label="previous"
                    position="absolute"
                    top="50%"
                    mr="3"
                    transform="translateY(-50%)"
                    _hover={{ bg: 'primary.500' }}
                    colorScheme="primary.200"
                    role="group"
                    icon={
                      <Icon
                        boxSize="6"
                        as={ChevronRight}
                        stroke={'text'}
                        _groupHover={{ stroke: 'white' }}
                      />
                    }
                    zIndex="docked"
                    onClick={() => {
                      prevSlide();
                    }}
                  />
                )}
                {current !== galleryImages.length && (
                  <IconButton
                    backgroundColor="primary.50"
                    aria-label="next"
                    position="absolute"
                    top="50%"
                    ml="3"
                    transform="translateY(-50%)"
                    left="0"
                    _hover={{ bg: 'primary.500' }}
                    role="group"
                    icon={
                      <Icon
                        boxSize="6"
                        as={ChevronLeft}
                        stroke={'text'}
                        _groupHover={{ stroke: 'white' }}
                      />
                    }
                    zIndex="docked"
                    marginInlineStart="16px"
                    onClick={() => {
                      nextSlide();
                    }}
                  />
                )}
              </Swiper>
            </Box>
            <Box
              display={{ md: 'none' }}
              sx={{
                '.swiper-slide': {
                  paddingInline: '5px',
                },
              }}
            >
              <Swiper
                onSwiper={setThumbsSwiper}
                slidesPerView={4}
                freeMode={true}
                watchSlidesProgress={true}
                modules={[FreeMode, Navigation, Thumbs]}
              >
                {galleryImages.map((img) => (
                  <SwiperSlide key={img}>
                    <GalleryThumbNailImage img={img} />
                  </SwiperSlide>
                ))}
              </Swiper>
            </Box>
            <Stack
              direction="row"
              gap="2"
              flexWrap={{ md: 'wrap' }}
              h="max-content"
              display={{ base: 'none', md: 'flex' }}
            >
              {galleryImages.map((img, index) => (
                <GalleryThumbNailImage
                  img={img}
                  key={img}
                  onClick={() => swiperRef?.current?.swiper.slideTo(index)}
                />
              ))}
            </Stack>
          </Stack>
        </>
      )}
    </TabPanel>
  );
};
export default GalleryModalTabPanel;
